'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { Shield, Mail, Globe, Sun, Moon, AlertTriangle, Key, ShieldCheck, Zap, Send, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';

interface Props {
  lang: 'vi' | 'en';
  onLangChange: (l: 'vi' | 'en') => void;
  darkMode: boolean;
  onDarkModeChange: (v: boolean) => void;
}

interface MagicLinkForm {
  email: string;
}

const t = {
  vi: {
    tagline: 'Bảo vệ tài liệu doanh nghiệp với mã hóa AES-256',
    subtitle: 'Hệ thống IRM & DLP hàng đầu cho tổ chức giáo dục',
    domainOnly: 'Chỉ dành cho tài khoản @vaschools.edu.vn',
    signIn: 'Đăng nhập',
    email: 'Email tổ chức',
    emailPlaceholder: 'name@vaschools.edu.vn',
    sendLink: 'Gửi Magic Link',
    sending: 'Đang gửi...',
    sentTitle: 'Kiểm tra hộp thư của bạn!',
    sentDesc: 'Chúng tôi đã gửi link đăng nhập đến',
    sentNote: 'Click vào link trong email để đăng nhập. Link có hiệu lực trong 1 giờ.',
    resend: 'Gửi lại',
    changeEmail: 'Đổi email',
    domainRestrict: 'Chỉ tài khoản @vaschools.edu.vn được phép truy cập',
    feature1: 'Mã hóa AES-256 tiêu chuẩn quân sự',
    feature2: 'Kiểm soát quyền truy cập chi tiết',
    feature3: 'Thu hồi quyền từ xa tức thì',
    feature4: 'Ngăn chặn rò rỉ dữ liệu DLP',
    invalidDomain: 'Email phải thuộc miền @vaschools.edu.vn',
    emailRequired: 'Vui lòng nhập email',
    sendError: 'Không thể gửi magic link. Vui lòng thử lại.',
    domainError: 'Tài khoản không thuộc miền @vaschools.edu.vn. Vui lòng dùng email tổ chức.',
    authError: 'Xác thực thất bại. Vui lòng thử lại.',
    otpExpiredTitle: 'Link đăng nhập đã hết hạn',
    otpExpiredDesc: 'Link trong email của bạn đã hết hạn hoặc không hợp lệ. Vui lòng nhập email để nhận link mới.',
    otpExpiredResend: 'Gửi link mới',
    otpExpiredResending: 'Đang gửi...',
    otpExpiredResent: 'Đã gửi link mới! Kiểm tra hộp thư của bạn.',
    backToLogin: 'Quay lại đăng nhập',
  },
  en: {
    tagline: 'Enterprise document protection with AES-256 encryption',
    subtitle: 'Leading IRM & DLP system for educational organizations',
    domainOnly: 'Restricted to @vaschools.edu.vn accounts only',
    signIn: 'Sign In',
    email: 'Organization email',
    emailPlaceholder: 'name@vaschools.edu.vn',
    sendLink: 'Send Magic Link',
    sending: 'Sending...',
    sentTitle: 'Check your inbox!',
    sentDesc: 'We sent a sign-in link to',
    sentNote: 'Click the link in your email to sign in. The link expires in 1 hour.',
    resend: 'Resend',
    changeEmail: 'Change email',
    domainRestrict: 'Only @vaschools.edu.vn accounts are permitted',
    feature1: 'Military-grade AES-256 encryption',
    feature2: 'Granular access permission control',
    feature3: 'Instant remote permission revocation',
    feature4: 'Data Loss Prevention (DLP) engine',
    invalidDomain: 'Email must belong to the @vaschools.edu.vn domain',
    emailRequired: 'Please enter your email',
    sendError: 'Failed to send magic link. Please try again.',
    domainError: 'Account is not from @vaschools.edu.vn domain. Please use your organization email.',
    authError: 'Authentication failed. Please try again.',
    otpExpiredTitle: 'Sign-in link has expired',
    otpExpiredDesc: 'The link in your email has expired or is invalid. Enter your email below to receive a fresh link.',
    otpExpiredResend: 'Send new link',
    otpExpiredResending: 'Sending...',
    otpExpiredResent: 'New link sent! Check your inbox.',
    backToLogin: 'Back to login',
  },
};

// Inner component that uses useSearchParams — must be wrapped in Suspense
function OAuthErrorHandler({ lang, onError, onOtpExpired }: { lang: 'vi' | 'en'; onError: (msg: string) => void; onOtpExpired: () => void }) {
  const searchParams = useSearchParams();

  useEffect(() => {
    const error = searchParams.get('error');
    const errorCode = searchParams.get('error_code');
    if (errorCode === 'otp_expired' || (error === 'access_denied' && errorCode === 'otp_expired')) {
      onOtpExpired();
    } else if (error === 'domain_restricted') {
      onError(lang === 'vi' ? t.vi.domainError : t.en.domainError);
    } else if (error === 'auth_failed' || error === 'no_code') {
      onError(lang === 'vi' ? t.vi.authError : t.en.authError);
    } else if (error) {
      onError(lang === 'vi' ? `Lỗi xác thực: ${error}` : `Authentication error: ${error}`);
    }
  }, [searchParams, lang, onError, onOtpExpired]);

  return null;
}

export default function SignUpLoginContent({ lang, onLangChange, darkMode, onDarkModeChange }: Props) {
  const tr = t[lang];
  const { signInWithMagicLink } = useAuth();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [expiredEmail, setExpiredEmail] = useState('');
  const [expiredResending, setExpiredResending] = useState(false);
  const [expiredSent, setExpiredSent] = useState(false);

  const { register, handleSubmit, getValues, formState: { errors } } = useForm<MagicLinkForm>({
    defaultValues: { email: '' },
  });

  const handleOtpExpired = () => {
    setOtpExpired(true);
    setLoginError('');
  };

  const handleExpiredResend = async () => {
    if (!expiredEmail || expiredResending) return;
    if (!expiredEmail.endsWith('@vaschools.edu.vn')) {
      toast.error(lang === 'vi' ? tr.invalidDomain : tr.invalidDomain);
      return;
    }
    setExpiredResending(true);
    try {
      await signInWithMagicLink(expiredEmail);
      setExpiredSent(true);
      toast.success(lang === 'vi' ? tr.otpExpiredResent : tr.otpExpiredResent);
    } catch {
      toast.error(tr.sendError);
    } finally {
      setExpiredResending(false);
    }
  };

  const onSubmit = async (data: MagicLinkForm) => {
    setLoginError('');
    setIsLoading(true);
    try {
      await signInWithMagicLink(data.email);
      setSentTo(data.email);
      toast.success(lang === 'vi' ? 'Magic link đã được gửi!' : 'Magic link sent!');
      // Redirect to verification screen to block dashboard access until confirmed
      router.push(`/verify-email?email=${encodeURIComponent(data.email)}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : tr.sendError;
      setLoginError(message.includes('vaschools') ? tr.invalidDomain : message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    const email = sentTo ?? getValues('email');
    if (!email) return;
    setIsLoading(true);
    try {
      await signInWithMagicLink(email);
      toast.success(lang === 'vi' ? 'Đã gửi lại magic link!' : 'Magic link resent!');
    } catch {
      toast.error(tr.sendError);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: Shield, label: tr.feature1 },
    { icon: Key, label: tr.feature2 },
    { icon: Zap, label: tr.feature3 },
    { icon: ShieldCheck, label: tr.feature4 },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Error handler for callback redirects */}
      <Suspense fallback={null}>
        <OAuthErrorHandler lang={lang} onError={setLoginError} onOtpExpired={handleOtpExpired} />
      </Suspense>

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] flex-shrink-0 gradient-brand p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <span className="font-800 text-xl text-white tracking-tight">IRMVault</span>
            <p className="text-white/70 text-[11px] font-500 tracking-wide">IRM & DLP Platform</p>
          </div>
        </div>

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mb-6">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-white/90 text-xs font-600 tracking-wide">@vaschools.edu.vn</span>
          </div>
          <h1 className="text-3xl xl:text-4xl font-800 text-white leading-tight mb-4">{tr.tagline}</h1>
          <p className="text-white/75 text-base leading-relaxed mb-10">{tr.subtitle}</p>
          <div className="space-y-3">
            {features.map((f, i) => {
              const FeatureIcon = f.icon;
              return (
                <div key={`feature-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                    <FeatureIcon size={15} className="text-white" />
                  </div>
                  <span className="text-white/85 text-sm font-500">{f.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { value: 'AES-256', label: 'Encryption' },
            { value: '99.9%', label: 'Uptime SLA' },
            { value: 'GDPR', label: 'Compliant' },
          ].map((s) => (
            <div key={`stat-${s.label}`} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-white font-700 text-sm">{s.value}</p>
              <p className="text-white/65 text-[11px] mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 relative">
        {/* Top controls */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={() => onLangChange(lang === 'vi' ? 'en' : 'vi')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-all duration-150 border border-border bg-card"
          >
            <Globe size={12} />
            {lang === 'vi' ? 'VI' : 'EN'}
          </button>
          <button
            onClick={() => onDarkModeChange(!darkMode)}
            className="p-2 rounded-xl text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-all duration-150 border border-border bg-card"
          >
            {darkMode ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>

        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Shield size={16} className="text-white" />
            </div>
            <span className="font-800 text-lg text-foreground">IRMVault</span>
          </div>

          {otpExpired ? (
            /* ── OTP Expired state ── */
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <AlertTriangle size={32} className="text-amber-500" />
              </div>
              <h2 className="text-2xl font-800 text-foreground mb-2">{tr.otpExpiredTitle}</h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{tr.otpExpiredDesc}</p>

              {expiredSent ? (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <CheckCircle size={24} className="text-primary" />
                  </div>
                  <p className="text-sm font-600 text-foreground">{tr.otpExpiredResent}</p>
                  <button
                    onClick={() => { setOtpExpired(false); setExpiredSent(false); setExpiredEmail(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                  >
                    {tr.backToLogin}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <input
                    type="email"
                    value={expiredEmail}
                    onChange={(e) => setExpiredEmail(e.target.value)}
                    placeholder={tr.emailPlaceholder}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  <button
                    onClick={handleExpiredResend}
                    disabled={expiredResending || !expiredEmail}
                    className="w-full py-3 text-sm font-700 text-white bg-primary hover:bg-primary/90 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {expiredResending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {tr.otpExpiredResending}
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} />
                        {tr.otpExpiredResend}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { setOtpExpired(false); setLoginError(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                  >
                    {tr.backToLogin}
                  </button>
                </div>
              )}
            </div>
          ) : sentTo ? (
            /* ── Sent confirmation state ── */
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-primary" />
              </div>
              <h2 className="text-2xl font-800 text-foreground mb-2">{tr.sentTitle}</h2>
              <p className="text-sm text-muted-foreground mb-1">{tr.sentDesc}</p>
              <p className="text-sm font-600 text-foreground mb-4 break-all">{sentTo}</p>
              <p className="text-xs text-muted-foreground mb-8 leading-relaxed">{tr.sentNote}</p>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleResend}
                  disabled={isLoading}
                  className="w-full py-3 text-sm font-700 text-white bg-primary hover:bg-primary/90 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  {isLoading ? tr.sending : tr.resend}
                </button>
                <button
                  onClick={() => setSentTo(null)}
                  className="w-full py-3 text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded-xl transition-all border border-border"
                >
                  {tr.changeEmail}
                </button>
              </div>
            </div>
          ) : (
            /* ── Login form ── */
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-800 text-foreground mb-1">{tr.signIn}</h2>
                <p className="text-sm text-muted-foreground">{tr.domainOnly}</p>
              </div>

              {loginError && (
                <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-3 mb-5">
                  <AlertTriangle size={15} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive leading-relaxed">{loginError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-xs font-600 text-muted-foreground mb-1.5">{tr.email}</label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register('email', {
                        required: tr.emailRequired,
                        validate: (v) => v.endsWith('@vaschools.edu.vn') || tr.invalidDomain,
                      })}
                      type="email"
                      placeholder={tr.emailPlaceholder}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-muted/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1.5">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 text-sm font-700 text-white bg-primary hover:bg-primary/90 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {tr.sending}
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      {tr.sendLink}
                    </>
                  )}
                </button>
              </form>

              <p className="text-[11px] text-muted-foreground text-center mt-6 leading-relaxed">
                {tr.domainRestrict}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}